<template>
  <div v-if="isAuthLoading" class="home-auth-buffer" aria-hidden="true"></div>
  <Dashboard v-else-if="isAuthenticated" />
  <main v-else class="home">
    <section class="hero">
      <div class="hero-copy">
        <h1
          class="hero-headline"
          :class="{
            'hero-headline--typing': isTyping,
            'hero-headline--complete': showBlinkingCursor,
          }"
          :aria-label="$t('home.headline')"
        >
          {{ displayedHeadline }}
        </h1>
        <p class="intro">{{ $t("home.subheadline") }}</p>
        <p class="availability">{{ $t("home.availability") }}</p>
        <div class="hero-tags" aria-hidden="true">
          <span class="hero-tag">PDF</span>
          <span class="hero-tag">Audio</span>
          <span class="hero-tag">YouTube</span>
          <span class="hero-tag">Flashcards</span>
          <span class="hero-tag">Quiz</span>
          <span class="hero-tag">Mind Maps</span>
        </div>
        <div class="app-links">
          <a :href="getPlayStoreUrl()" class="store-link">
            <img
              style="height: 73px; width: 186px; margin-right: -12px;"
              src="./../assets/android.png"
              :alt="$t('home.downloadOn') + ' App Store'"
            />
          </a>
          <a :href="getAppStoreUrl()" class="store-link">
            <img
              style="height: 59px; width: 149px;"
              src="./../assets/ios.svg"
              :alt="$t('home.downloadOn') + ' App Store'"
            />
          </a>
          <router-link
            to="/signin"
            class="store-link store-link--web"
            :aria-label="$t('home.openOn') + ' ' + $t('home.web')"
          >
            <span class="web-store-button" aria-hidden="true">
              <svg
                class="web-store-button__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.9"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="9.5" />
                <path d="M2.5 12h19" />
                <path d="M12 2.5c2.5 2.6 3.9 6 3.9 9.5s-1.4 6.9-3.9 9.5c-2.5-2.6-3.9-6-3.9-9.5S9.5 5.1 12 2.5z" />
              </svg>
              <span class="web-store-button__text">
                <span class="web-store-button__small">{{ $t("home.openOn") }}</span>
                <span class="web-store-button__big">{{ $t("home.web") }}</span>
              </span>
            </span>
          </router-link>
        </div>
      </div>
    </section>

    <section id="features" class="content-section">
      <div class="section-heading">
        <h2>{{ $t("home.featuresTitle") }}</h2>
      </div>
      <div class="screenshot-row" :aria-label="`${brandName} screenshots`">
        <div
          class="screenshot-item"
          v-for="(screenshotUrl, index) in screenshotUrls"
          :key="screenshotUrl"
        >
          <img
            :src="screenshotUrl"
            :alt="`${brandName} screenshot ${index + 1}`"
            class="screenshot-image"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <section class="content-section trust-section" aria-labelledby="home-trust-title">
      <div class="section-heading">
        <p class="section-kicker">{{ $t("home.trustKicker") }}</p>
        <h2 id="home-trust-title">{{ $t("home.trustTitle") }}</h2>
      </div>
      <div class="trust-band">
        <article class="trust-stat">
          <span>{{ $t("home.adoptedByKicker") }}</span>
          <strong ref="studentsCounter">{{ studentsCountText }}</strong>
          <span>{{ $t("home.studentsLabel") }}</span>
        </article>
        <article class="trust-stat trust-stat--rating">
          <span>1.000 {{ $t("home.review") }}</span>
          <img src="./../assets/laurel.svg" class="trust-laurel trust-laurel--left" alt="" aria-hidden="true" />
          <strong>4.6</strong>
          <img src="./../assets/laurel.svg" class="trust-laurel trust-laurel--right" alt="" aria-hidden="true" />
          <span class="trust-stars" aria-hidden="true">★★★★★</span>
        </article>
      </div>
    </section>

    <section id="how-it-works" class="content-section">
      <div class="section-heading">
        <h2>{{ $t("home.howItWorksTitle") }}</h2>
      </div>
      <div class="steps-grid">
        <article
          class="step-card interactive-card"
          :class="{ 'step-card--illustrated': showStepIllustrations }"
          v-for="item in stepItems"
          :key="item.index"
          @pointermove="onInteractivePointerMove"
          @pointerleave="onInteractivePointerLeave"
        >
          <span class="step-ghost-number" aria-hidden="true">{{ item.index }}</span>
          <div class="step-card-body">
            <span class="step-label" aria-hidden="true">
              <span class="step-label-dot"></span>
              <span>{{ item.index }}</span>
            </span>
            <h3 class="step-card-title">{{ $t(item.title) }}</h3>
            <p>{{ $t(item.text) }}</p>
          </div>
          <!-- The app in hand, at the step it describes. The screens are in
               French, so they only appear for French readers. -->
          <div v-if="showStepIllustrations" class="step-illustration" aria-hidden="true">
            <img :src="item.image" alt="" loading="lazy" decoding="async" />
          </div>
        </article>
      </div>
    </section>

    <section class="content-section seo-section" aria-labelledby="home-seo-title">
      <div class="section-heading">
        <h2 id="home-seo-title">{{ $t("home.seoTitle") }}</h2>
      </div>
      <div class="seo-copy">
        <p>{{ $t("home.seoIntro") }}</p>
        <h3>{{ $t("home.seoBlock1Title") }}</h3>
        <p>{{ $t("home.seoBlock1Text") }}</p>
        <h3>{{ $t("home.seoBlock2Title") }}</h3>
        <p>{{ $t("home.seoBlock2Text") }}</p>
        <h3>{{ $t("home.seoBlock3Title") }}</h3>
        <p>{{ $t("home.seoBlock3Text") }}</p>
        <h3>{{ $t("home.seoBlock4Title") }}</h3>
        <p>{{ $t("home.seoBlock4Text") }}</p>
        <h3>{{ $t("home.seoBlock5Title") }}</h3>
        <p>{{ $t("home.seoBlock5Text") }}</p>
        <p>{{ $t("home.seoOutro") }}</p>
      </div>
      <nav class="seo-links" :aria-label="$t('home.seoLinksTitle')">
        <h3>{{ $t("home.seoLinksTitle") }}</h3>
        <ul>
          <li><a href="#features">{{ $t("home.seoLinkFeatures") }}</a></li>
          <li><a href="#how-it-works">{{ $t("home.seoLinkHow") }}</a></li>
          <li><a href="#faq">{{ $t("home.seoLinkFaq") }}</a></li>
          <li><router-link to="/help">{{ $t("home.seoLinkHelp") }}</router-link></li>
          <li><router-link to="/signin">{{ $t("home.seoLinkSignIn") }}</router-link></li>
          <li><router-link to="/privacy">{{ $t("home.seoLinkPrivacy") }}</router-link></li>
          <li><router-link to="/terms">{{ $t("home.seoLinkTerms") }}</router-link></li>
        </ul>
      </nav>
    </section>

    <section id="faq" class="content-section faq-section home-faq-section">
      <div class="section-heading home-faq-heading">
        <h2>{{ $t("home.faqTitle") }}</h2>
      </div>
      <div class="home-faq-list">
        <details
          v-for="(item, index) in faqItems"
          :key="item.questionKey"
          ref="faqItems"
          class="home-faq-item"
          @toggle="onFaqToggle($event, index)"
        >
          <summary class="home-faq-question">
            <span class="home-faq-index" aria-hidden="true">{{ String(index + 1).padStart(2, "0") }}</span>
            <span class="home-faq-text">{{ $t(item.questionKey) }}</span>
            <span class="home-faq-icon" aria-hidden="true"></span>
          </summary>
          <div class="home-faq-answer-wrap">
            <p class="home-faq-answer">{{ $t(item.answerKey) }}</p>
          </div>
        </details>
      </div>
    </section>
    <AppFooter />
  </main>
</template>

<script>
import stepSourceImage from "@/assets/source.webp";
import stepStructuredImage from "@/assets/structured.webp";
import stepReviseImage from "@/assets/revize.webp";
import AppFooter from "../components/AppFooter.vue";
import Dashboard from "./Dashboard.vue";
import { authState } from "../stores/authStore";

const screenshotContext = require.context(
  "../screenshots",
  true,
  /iPhone[^/]*\.webp$/
);

const screenshotsByLocale = (() => {
  const map = {};
  for (const key of screenshotContext.keys()) {
    const match = key.match(/^\.\/([^/]+)\/(.+)$/);
    if (!match) continue;
    const [, folder, filename] = match;
    if (!map[folder]) map[folder] = [];
    map[folder].push({ filename, url: screenshotContext(key) });
  }
  for (const folder of Object.keys(map)) {
    map[folder].sort((a, b) => a.filename.localeCompare(b.filename));
    map[folder] = map[folder].map((entry) => entry.url);
  }
  return map;
})();

const localeToFolder = {
  en: "en-US", "en-us": "en-US", "en-ca": "en-US", "en-gb": "en-US",
  fr: "fr-FR", "fr-fr": "fr-FR", "fr-ca": "fr-FR", "fr-be": "fr-FR", "fr-ch": "fr-FR",
  ja: "ja", "ja-jp": "ja",
  de: "de-DE", "de-de": "de-DE", "de-at": "de-DE", "de-ch": "de-DE",
  it: "it", "it-it": "it",
  es: "es-ES", "es-es": "es-ES", "es-mx": "es-ES", "es-ar": "es-ES",
  pt: "pt-PT", "pt-pt": "pt-PT", "pt-br": "pt-PT",
  nl: "nl-NL", "nl-nl": "nl-NL",
  pl: "pl",
  ru: "ru",
  tr: "tr",
  sv: "sv",
  uk: "uk",
  ro: "ro",
  el: "el",
  cs: "cs",
  hi: "hi",
  ar: "ar-SA", "ar-sa": "ar-SA",
};

export default {
  name: "HomePage",
  components: {
    AppFooter,
    Dashboard,
  },
  data() {
    return {
      displayedHeadline: "",
      typingTimer: null,
      isTyping: false,
      showBlinkingCursor: false,
      enableCursorInteractions: false,
      screenshotUrls: [],
      // The "adopted by" figure as it counts up on first sight — null until
      // the band has scrolled into view, so it shows the final value on a
      // plain render (and to crawlers).
      studentsDisplayed: null,
      studentsCounterFrame: null,
      studentsObserver: null,
      studentsCounterDone: false,
      studentsScrollFallback: null,
    };
  },
  computed: {
    // The localized figure, split so the count-up keeps the locale's own
    // prefix, suffix and thousands separator ("+80.000", "80,000+",
    // "+80 000") and only the digits move.
    studentsCountParts() {
      const raw = String(this.$t("home.studentsCount"));
      const match = raw.match(/^(\D*)([\d.,\s\u00a0]+?)(\D*)$/);
      if (!match) return { prefix: "", value: 0, separator: "", suffix: raw };
      const digitsOnly = match[2].replace(/\D/g, "");
      const separator = (match[2].match(/\D/) || [""])[0];
      return {
        prefix: match[1],
        value: Number(digitsOnly) || 0,
        separator,
        suffix: match[3],
      };
    },
    studentsCountText() {
      const { prefix, value, separator, suffix } = this.studentsCountParts;
      const shown = this.studentsDisplayed === null ? value : this.studentsDisplayed;
      const grouped = String(shown).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      return `${prefix}${grouped}${suffix}`;
    },
    isAuthenticated() {
      return authState.isAuthenticated;
    },
    isAuthLoading() {
      return authState.isLoading;
    },
    brandName() {
      const rawLocale = this.$i18n?.locale;
      const locale = (typeof rawLocale === "string" ? rawLocale : rawLocale?.value) || "en";
      return locale.toLowerCase().startsWith("fr") ? "Wellnotes" : "Wellnotes";
    },
    featureItems() {
      return [
        {
          icon: "↗",
          title: "home.featureOneTitle",
          text: "home.featureOneText",
        },
        {
          icon: "◉",
          title: "home.featureTwoTitle",
          text: "home.featureTwoText",
        },
        {
          icon: "✓",
          title: "home.featureThreeTitle",
          text: "home.featureThreeText",
        },
      ];
    },
    showStepIllustrations() {
      const rawLocale = this.$i18n?.locale;
      const locale = (typeof rawLocale === "string" ? rawLocale : rawLocale?.value) || "en";
      return locale.toLowerCase().startsWith("fr");
    },
    stepItems() {
      return [
        {
          index: "01",
          title: "home.stepOneTitle",
          text: "home.stepOneText",
          image: stepSourceImage,
        },
        {
          index: "02",
          title: "home.stepTwoTitle",
          text: "home.stepTwoText",
          image: stepStructuredImage,
        },
        {
          index: "03",
          title: "home.stepThreeTitle",
          text: "home.stepThreeText",
          image: stepReviseImage,
        },
      ];
    },
    faqItems() {
      return [
        { questionKey: "home.faqOneQuestion", answerKey: "home.faqOneAnswer" },
        { questionKey: "home.faqTwoQuestion", answerKey: "home.faqTwoAnswer" },
        { questionKey: "home.faqThreeQuestion", answerKey: "home.faqThreeAnswer" },
        { questionKey: "home.faqFourQuestion", answerKey: "home.faqFourAnswer" },
        { questionKey: "home.faqFiveQuestion", answerKey: "home.faqFiveAnswer" },
      ];
    },
  },
  mounted() {
    this.updateCursorInteractionSupport();
    this.startHeadlineTyping();
    this.fetchAppScreenshots();
    this.watchStudentsCounter();
  },
  beforeUnmount() {
    this.clearTypingTimer();
    this.stopStudentsCounter();
  },
  watch: {
    "$i18n.locale"() {
      this.startHeadlineTyping();
      this.fetchAppScreenshots();
    },
  },
  methods: {
    // Starts the count-up the first time the figure scrolls into view, then
    // lets go of the observer: it runs once per visit.
    //
    // Armed only once the page has finished loading. At mount the
    // screenshot row above this block has no images yet, so the block sits
    // far higher than it will, briefly inside the viewport — and an
    // observer armed then fired at once, running the whole count while the
    // reader was still looking at the hero. The bottom margin asks for the
    // figure to be well inside the viewport, not just past its edge.
    watchStudentsCounter(attempt = 0) {
      if (typeof IntersectionObserver === "undefined") return;
      // The figure is not in the DOM at mount — the section renders once
      // its data is in — so an early call found no element and armed
      // nothing, silently. Retried until the element exists.
      if (!this.$refs.studentsCounter) {
        if (attempt < 50) setTimeout(() => this.watchStudentsCounter(attempt + 1), 200);
        return;
      }
      // Confirmed, not taken at its word: the screenshot row above loads
      // its images after mount, and each arrival pushes this block down. An
      // intersection reported during that shuffle is re-measured 400 ms
      // later; if the block has moved out of view meanwhile, the observer
      // stays armed for the real arrival.
      const stillInView = () => {
        const node = this.$refs.studentsCounter;
        if (!node) return false;
        const rect = node.getBoundingClientRect();
        const viewportBottom = window.innerHeight * 0.85;
        const visible = Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, 0);
        return rect.height > 0 && visible / rect.height >= 0.6;
      };
      const arm = () => {
        if (this.studentsObserver || !this.$refs.studentsCounter) return;
        let confirming = false;
        this.studentsObserver = new IntersectionObserver(
          (entries) => {
            const seen = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6);
            if (!seen || confirming) return;
            confirming = true;
            setTimeout(() => {
              confirming = false;
              if (!this.studentsObserver || !stillInView()) return;
              this.stopStudentsCounter();
              this.runStudentsCounter();
            }, 400);
          },
          { threshold: [0.6], rootMargin: "0px 0px -15% 0px" },
        );
        this.studentsObserver.observe(this.$refs.studentsCounter);
      };
      // A timeout rather than requestAnimationFrame: a frame callback never
      // fires in a background tab, and a page opened behind another would
      // then never arm at all.
      if (document.readyState === "complete") {
        setTimeout(arm, 50);
      } else {
        window.addEventListener("load", () => setTimeout(arm, 50), { once: true });
      }
      // Belt and braces: a plain scroll check alongside the observer, so a
      // browser that never reports the intersection still starts the count
      // the moment the figure is read.
      this.studentsScrollFallback = () => {
        if (this.studentsCounterDone || !stillInView()) return;
        this.stopStudentsCounter();
        this.runStudentsCounter();
      };
      window.addEventListener("scroll", this.studentsScrollFallback, { passive: true });
    },
    // From half the figure, over a couple of seconds, eased out so the last
    // thousands land slowly enough to be read. Half rather than zero: the
    // count stays believable, and the leading digit still moves.
    runStudentsCounter() {
      const total = this.studentsCountParts.value;
      if (!total || this.studentsCounterDone) {
        this.studentsDisplayed = null;
        return;
      }
      this.studentsCounterDone = true;
      const start = Math.floor(total / 2);
      const duration = 2200;
      const startedAt = performance.now();
      this.studentsDisplayed = start;
      const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.studentsDisplayed = Math.round(start + (total - start) * eased);
        if (progress < 1) {
          this.studentsCounterFrame = requestAnimationFrame(step);
        } else {
          this.studentsDisplayed = null;
          this.studentsCounterFrame = null;
        }
      };
      this.studentsCounterFrame = requestAnimationFrame(step);
    },
    stopStudentsCounter() {
      if (this.studentsObserver) {
        this.studentsObserver.disconnect();
        this.studentsObserver = null;
      }
      if (this.studentsScrollFallback) {
        window.removeEventListener("scroll", this.studentsScrollFallback);
        this.studentsScrollFallback = null;
      }
      if (this.studentsCounterFrame) {
        cancelAnimationFrame(this.studentsCounterFrame);
        this.studentsCounterFrame = null;
      }
    },
    getAppStoreUrl() {
      return this.$t("home.appStoreUrl");
    },
    getPlayStoreUrl() {
      return "https://play.google.com/store/apps/details?id=com.antoine.cogito";
    },
    onFaqToggle(event, index) {
      if (!event.target.open) {
        return;
      }
      const items = this.$refs.faqItems;
      if (!items) {
        return;
      }
      items.forEach((item, i) => {
        if (i !== index && item.open) {
          item.open = false;
        }
      });
    },
    clearTypingTimer() {
      if (!this.typingTimer) {
        return;
      }

      clearTimeout(this.typingTimer);
      this.typingTimer = null;
    },
    startHeadlineTyping() {
      this.clearTypingTimer();

      const headline = this.$t("home.headline");
      let index = 0;

      this.displayedHeadline = "";
      this.isTyping = true;
      this.showBlinkingCursor = false;

      const typeNextCharacter = () => {
        this.displayedHeadline = headline.slice(0, index);

        if (index >= headline.length) {
          this.typingTimer = setTimeout(() => {
            this.isTyping = false;
            this.showBlinkingCursor = true;
            this.typingTimer = null;
          }, 1800);
          return;
        }

        index += 1;
        this.typingTimer = setTimeout(typeNextCharacter, 52);
      };

      typeNextCharacter();
    },
    updateCursorInteractionSupport() {
      if (typeof window === "undefined") {
        this.enableCursorInteractions = false;
        return;
      }

      const hasFinePointer = window.matchMedia("(pointer: fine)").matches;
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      this.enableCursorInteractions = hasFinePointer && !prefersReducedMotion;
    },
    onInteractivePointerMove(event) {
      if (!this.enableCursorInteractions) {
        return;
      }

      const card = event.currentTarget;

      if (!(card instanceof HTMLElement)) {
        return;
      }

      const rect = card.getBoundingClientRect();
      const relativeX = event.clientX - rect.left;
      const relativeY = event.clientY - rect.top;
      const xPercent = rect.width > 0 ? relativeX / rect.width : 0.5;
      const yPercent = rect.height > 0 ? relativeY / rect.height : 0.5;
      const rotateY = (xPercent - 0.5) * 4;
      const rotateX = (0.5 - yPercent) * 4;

      card.style.setProperty("--cursor-x", `${relativeX}px`);
      card.style.setProperty("--cursor-y", `${relativeY}px`);
      card.style.setProperty("--tilt-x", `${rotateX.toFixed(2)}deg`);
      card.style.setProperty("--tilt-y", `${rotateY.toFixed(2)}deg`);
    },
    onInteractivePointerLeave(event) {
      const card = event.currentTarget;

      if (!(card instanceof HTMLElement)) {
        return;
      }

      card.style.setProperty("--tilt-x", "0deg");
      card.style.setProperty("--tilt-y", "0deg");
      card.style.removeProperty("--cursor-x");
      card.style.removeProperty("--cursor-y");
    },
    fetchAppScreenshots() {
      const rawLocale = this.$i18n?.locale;
      const locale = (typeof rawLocale === "string" ? rawLocale : rawLocale?.value) || "en";
      const key = locale.toLowerCase();
      const folder =
        localeToFolder[key] ||
        localeToFolder[key.split("-")[0]] ||
        "en-US";

      const screenshots = screenshotsByLocale[folder] || screenshotsByLocale["en-US"] || [];
      this.screenshotUrls = screenshots.slice(1);
    },
  },
};
</script>

<style scoped>
/* Buffer shown while Firebase is still resolving the auth state, so we
   don't flash the landing page to a signed-in user (or vice versa).
   Solid white in light mode, solid black in dark — covers the full
   viewport including any app-level gradient underneath. */
.home-auth-buffer {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #ffffff;
}

@media (prefers-color-scheme: dark) {
  .home-auth-buffer {
    background: #000000;
  }
}

.home {
  width: min(1120px, calc(100% - 32px));
  padding: 20px 0 120px;
  color: var(--app-text);
}

.hero {
  display: grid;
  gap: 28px;
  align-items: start;
  padding: 24px 0 0;
}

.hero-copy {
  position: relative;
  padding: 24px 0 0;
  text-align: center;
}

.hero-copy::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background:
    radial-gradient(circle at top, color-mix(in srgb, var(--app-accent) 14%, transparent), transparent 58%),
    linear-gradient(180deg, var(--app-surface) 0%, transparent 100%);
  opacity: 0.95;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 16px;
  padding: 6px 10px;
  border: 1px solid var(--app-border);
  border-radius: 999px;
  color: var(--app-text-muted);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.intro,
.availability {
  max-width: 760px;
  margin: 0 auto;
  color: var(--app-text-muted);
}

.intro {
  font-size: 18px;
  line-height: 1.7;
}

.availability {
  margin-top: 14px;
  font-size: 15px;
}

.hero-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  max-width: 720px;
  margin: 24px auto 0;
}

.hero-tag {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--app-border);
  border-radius: 999px;
  background: color-mix(in srgb, var(--app-surface-strong) 76%, transparent);
  color: var(--app-text);
  font-size: 14px;
  font-weight: 600;
}

.app-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 28px;
}

.store-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  transition: transform 160ms ease, opacity 160ms ease;
}

.store-link:hover {
  transform: translateY(-1px);
}

.store-link:active {
  transform: translateY(0);
}

/* Web equivalent of the iOS "Download on the App Store" pill. Targets the
   exact metrics of Apple's badge (124×49 with ~8px radius, white outline,
   black fill) so it pairs visually with the iOS SVG. */
.web-store-button {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: 149px;
  height: 50px;
  padding: 0 14px;
  border-radius: 10px;
  background: #000;
  color: #fff;
  border: 1px solid rgb(166, 166, 166);
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  text-decoration: none;
  box-sizing: border-box;
}

.web-store-button__icon {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  color: #fff;
}

.web-store-button__text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1;
  flex: 1;
}

.web-store-button__small {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.01em;
  margin-bottom: 1px;
  white-space: nowrap;
}

.web-store-button__big {
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
}

.store-link--web {
  text-decoration: none;
  color: inherit;
  /* The base `.store-link` sets `line-height: 0` to crop the iOS image.
     The web button has real two-line text, so restore a normal line-height. */
  line-height: 1;
}

.hero-highlights,
.feature-grid {
  display: flex;
  gap: 18px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-bottom: 8px;
  scroll-snap-type: x proximity;
}

.feature-grid .feature-card {
  flex: 0 0 clamp(140px, 16vw, 200px);
  scroll-snap-align: start;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

h1 {
  max-width: 920px;
  margin: 0 auto 16px;
  font-size: clamp(34px, 5vw, 58px);
  line-height: 1.04;
}

.hero-headline {
  min-height: 2.2em;
}

.hero-headline--typing::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 0.9em;
  margin-left: 6px;
  background: currentColor;
  vertical-align: baseline;
}

.hero-headline--complete::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 0.9em;
  margin-left: 6px;
  background: currentColor;
  vertical-align: baseline;
  animation: blink-caret 1.2s steps(1) infinite;
}

p {
  font-size: 16px;
}

.content-section {
  margin-top: 12px;
}

.section-heading {
  display: grid;
  gap: 8px;
  margin-bottom: 22px;
  text-align: center;
}

.section-kicker {
  margin: 0;
  color: var(--app-text-muted);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.content-section h2,
.highlight-card h2 {
  margin: 0;
  font-size: clamp(28px, 3vw, 36px);
  line-height: 1.1;
}

.highlight-card,
.feature-card,
.step-card,
.faq-item {
  border: 1px solid var(--app-border);
  border-radius: 8px;
  background: linear-gradient(180deg, var(--app-surface-strong) 0%, var(--app-surface) 100%);
  box-shadow: var(--app-shadow);
}

.interactive-card {
  position: relative;
  isolation: isolate;
  overflow: hidden;
  transform: perspective(960px) rotateX(var(--tilt-x, 0deg)) rotateY(var(--tilt-y, 0deg))
    translateY(var(--lift-y, 0));
  transition: transform 180ms ease, border-color 180ms ease;
  will-change: transform;
}

.interactive-card::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: radial-gradient(
    220px circle at var(--cursor-x, 50%) var(--cursor-y, 50%),
    color-mix(in srgb, var(--app-accent) 16%, transparent),
    transparent 62%
  );
  opacity: 0;
  transition: opacity 160ms ease;
  pointer-events: none;
}

.interactive-card:hover {
  --lift-y: -2px;
  border-color: color-mix(in srgb, var(--app-accent) 40%, var(--app-border));
}

.interactive-card:hover::before {
  opacity: 1;
}

.highlight-card,
.feature-card,
.step-card {
  padding: 22px;
}

.highlight-card {
  display: grid;
  gap: 14px;
  min-height: 100%;
}

.highlight-card h2,
.feature-card h3,
.step-card h3 {
  font-size: 22px;
}

.highlight-card p,
.feature-card p,
.step-card p,
.faq-item p {
  margin: 0;
  color: var(--app-text-muted);
  line-height: 1.7;
}

.highlight-icon,
.feature-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--app-accent) 16%, transparent);
  color: var(--app-text);
  font-size: 18px;
  font-weight: 700;
}

.feature-card {
  display: grid;
  gap: 16px;
}

.feature-image-container {
  width: 100%;
  aspect-ratio: 9 / 16;
  border-radius: 8px;
  overflow: hidden;
  background: var(--app-surface-strong);
}

.feature-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.screenshot-row {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 14px;
  overflow: visible;
  perspective: 900px;
}

.screenshot-item {
  position: relative;
  min-width: 0;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--app-border);
  background: var(--app-surface-strong);
  display: flex;
  align-items: flex-start;
  transform: translateY(0) scale(1);
  transform-origin: center center;
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    border-color 180ms ease,
    opacity 180ms ease;
  will-change: transform;
}

.screenshot-row:hover .screenshot-item {
  opacity: 0.66;
}

.screenshot-row .screenshot-item:hover {
  z-index: 3;
  opacity: 1;
  border-color: color-mix(in srgb, var(--app-accent) 48%, var(--app-border));
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.22);
  transform: scale(1.12);
}

.screenshot-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.trust-section {
  margin-top: 42px;
}

.trust-band {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: 560px;
  margin: 0 auto;
  padding: 34px 18px;
  border: 1px solid var(--app-border);
  border-radius: 24px;
  box-shadow: var(--app-shadow);
}

.trust-stat {
  display: grid;
  gap: 6px;
  padding: 4px 12px;
  text-align: center;
}
.trust-stat + .trust-stat {
  border-left: 1px solid var(--app-border);
}
.trust-stat--rating {
  grid-template-columns: auto auto auto;
  grid-template-rows: auto auto auto;
  row-gap: 6px;
  column-gap: 10px;
  justify-content: center;
  align-items: center;
}
.trust-stat--rating > span:first-child {
  grid-row: 1;
  grid-column: 1 / 4;
  color: var(--app-heading);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.trust-laurel {
  grid-row: 2;
  align-self: end;
  height: 56px;
  width: auto;
  filter: brightness(0) invert(1);
}
/* Black on the light theme: white laurels vanished on the white page. */
html[data-theme="light"] .trust-laurel {
  filter: brightness(0);
}
.trust-laurel--left {
  grid-column: 1;
  justify-self: end;
}
.trust-laurel--right {
  grid-column: 3;
  justify-self: start;
  transform: scaleX(-1);
}
.trust-stat--rating strong {
  grid-row: 2;
  grid-column: 2;
}
.trust-band .trust-stat--rating .trust-stars {
  grid-row: 3;
  grid-column: 1 / 4;
  font-size: 16px;
  color: var(--app-heading);
  letter-spacing: 2px;
  text-transform: none;
}
/* The rating block's own `.trust-stat--rating > span:first-child` rule is
   more specific and wins there, so this only styles the plain stat's kicker
   ("adopté par") — kept as one shared caption look between the two. */
.trust-stat > span:first-child {
  color: var(--app-heading);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.trust-stat strong {
  font-size: clamp(36px, 5vw, 48px);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  color: var(--app-heading);
}
.trust-stat span:last-child {
  color: var(--app-heading);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
@media (max-width: 640px) {
  .trust-band {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 28px 16px;
  }
  .trust-stat + .trust-stat {
    border-left: none;
    border-top: 1px solid var(--app-border);
    padding-top: 22px;
  }
}


@media (max-width: 900px) {
  .screenshot-row {
    display: flex;
    grid-template-columns: none;
    gap: 8px;
    overflow-x: auto;
    padding: 18px 0;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
  }

  .screenshot-item {
    flex: 0 0 auto;
    width: 60vw;
    max-width: 260px;
    scroll-snap-align: center;
  }

  .screenshot-row .screenshot-item:hover {
    transform: scale(1.04);
  }

  .trust-grid {
    grid-template-columns: 1fr;
  }
}

.feature-card-top {
  display: flex;
  align-items: center;
  gap: 14px;
}

.feature-card-top h3 {
  margin: 0;
}

.faq-section {
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
}

.faq-list {
  display: grid;
  gap: 14px;
}

.faq-item {
  padding: 0;
  overflow: hidden;
}

.faq-item summary {
  position: relative;
  padding: 20px 48px 20px 20px;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  list-style: none;
  color: var(--app-text);
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item summary::after {
  content: "+";
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  color: var(--app-text-muted);
  font-size: 24px;
  line-height: 1;
}

.faq-item[open] summary::after {
  content: "−";
}

.faq-item p {
  padding: 0 20px 20px;
}

.home-faq-section {
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
}

.home-faq-heading {
  text-align: center;
  margin-bottom: 28px;
}

.home-faq-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid var(--app-border);
}

.home-faq-item {
  border-bottom: 1px solid var(--app-border);
  background: transparent;
}

.home-faq-question {
  display: grid;
  grid-template-columns: 64px 1fr auto;
  align-items: center;
  gap: 20px;
  padding: 26px 4px;
  cursor: pointer;
  list-style: none;
  color: var(--app-heading);
  font-size: 20px;
  font-weight: 600;
  line-height: 1.35;
  transition: color 0.2s ease;
}

.home-faq-question::-webkit-details-marker {
  display: none;
}

.home-faq-item:hover .home-faq-question,
.home-faq-item[open] .home-faq-question {
  color: var(--app-link);
}

.home-faq-index {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: color-mix(in srgb, var(--app-text-muted) 70%, transparent);
  font-variant-numeric: tabular-nums;
}

.home-faq-item[open] .home-faq-index {
  color: var(--app-link);
}

.home-faq-text {
  min-width: 0;
}

.home-faq-icon {
  position: relative;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--app-text-muted);
  transition: color 0.2s ease, transform 0.25s ease;
}

.home-faq-icon::before,
.home-faq-icon::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 14px;
  height: 1.5px;
  background: currentColor;
  border-radius: 2px;
  transform: translate(-50%, -50%);
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.home-faq-icon::after {
  transform: translate(-50%, -50%) rotate(90deg);
}

.home-faq-item[open] .home-faq-icon {
  color: var(--app-link);
  transform: rotate(45deg);
}

.home-faq-answer-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.28s ease;
}

.home-faq-item[open] .home-faq-answer-wrap {
  grid-template-rows: 1fr;
}

.home-faq-answer-wrap > .home-faq-answer {
  overflow: hidden;
  min-height: 0;
}

.home-faq-answer {
  margin: 0;
  padding: 0 0 24px 84px;
  color: var(--app-text-muted);
  font-size: 16px;
  line-height: 1.7;
  max-width: 720px;
}

@media (max-width: 640px) {
  .home-faq-question {
    grid-template-columns: 44px 1fr auto;
    gap: 12px;
    padding: 20px 2px;
    font-size: 17px;
  }

  .home-faq-index {
    font-size: 13px;
  }

  .home-faq-answer {
    padding-left: 56px;
    padding-bottom: 20px;
    font-size: 15px;
  }
}

@media (max-width: 900px) {
  .hero-highlights,
  .feature-grid,
  .steps-grid {
    grid-template-columns: 1fr;
  }

  .home {
    width: min(100%, calc(100% - 24px));
  }

  .hero-copy {
    padding: 18px 16px 0;
  }

  .content-section {
    margin-top: 10px;
  }

  .content-section h2,
  .highlight-card h2 {
    font-size: 26px;
  }
}

@media (pointer: coarse), (prefers-reduced-motion: reduce) {
  .interactive-card {
    transform: none;
    transition: border-color 180ms ease;
  }

  .interactive-card::before {
    display: none;
  }
}

@keyframes blink-caret {
  0%,
  49% {
    opacity: 1;
  }

  50%,
  100% {
    opacity: 0;
  }
}

.hero-headline--complete::after {
  content: "";
  display: inline-block;
  width: 2px;
  height: 0.9em;
  margin-left: 6px;
  background: currentColor;
  vertical-align: baseline;
  animation: blink-caret 1.2s steps(1) infinite;
}

p {
  font-size: 16px;
}

.content-section {
  margin-top: 48px;
}

.content-section h2 {
  margin-bottom: 20px;
  font-size: 30px;
  text-align: center;
}

.feature-grid,
.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.feature-card,
.faq-item {
  border: 1px solid var(--app-border);
  border-radius: 20px;
  background: linear-gradient(180deg, var(--app-surface-strong) 0%, var(--app-surface) 100%);
  box-shadow: var(--app-shadow);
}

.feature-card {
  padding: 24px;
}

.feature-card h3 {
  margin: 0 0 12px;
  font-size: 20px;
}

.feature-card p,
.faq-item p {
  margin: 0;
  color: var(--app-text-muted);
  line-height: 1.7;
}

.steps-grid {
  gap: 20px;
}

.step-card {
  position: relative;
  overflow: hidden;
  min-height: 220px;
  padding: 28px 26px 28px;
  border: 1px solid var(--app-border);
  border-radius: 24px;
  background:
    radial-gradient(
      140% 80% at 100% 0%,
      color-mix(in srgb, var(--app-accent) 10%, transparent) 0%,
      transparent 55%
    ),
    var(--app-surface);
  box-shadow: none;
  transition: border-color 220ms ease, transform 220ms ease, box-shadow 220ms ease;
}

.step-card:hover {
  border-color: color-mix(in srgb, var(--app-accent) 45%, var(--app-border));
  box-shadow: 0 18px 40px -18px rgba(11, 95, 255, 0.28);
}

.step-ghost-number {
  position: absolute;
  top: -22px;
  right: -6px;
  font-size: 150px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  color: transparent;
  background: linear-gradient(
    160deg,
    color-mix(in srgb, var(--app-accent) 28%, transparent) 0%,
    color-mix(in srgb, var(--app-accent) 6%, transparent) 60%,
    transparent 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  pointer-events: none;
  user-select: none;
  transition: transform 320ms ease, opacity 220ms ease;
  opacity: 0.9;
}

.step-card:hover .step-ghost-number {
  transform: translate(-4px, 2px);
  opacity: 1;
}

/* French cards carry the app in hand: the text keeps the top, the phone
   rises from the bottom edge and fades into the card's own ground so it
   reads as part of the card rather than a photo pinned on it. */
.step-card--illustrated {
  box-sizing: border-box;
  min-height: 520px;
  padding-bottom: 300px;
}

/* The body stretches to the card's full height elsewhere; here that
   height includes the room reserved for the phone, and the text would
   spread down into it. */
.step-card--illustrated .step-card-body {
  height: auto;
}

.step-illustration {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -6px;
  height: 320px;
  overflow: hidden;
  pointer-events: none;
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, #000 22%, #000 100%);
  mask-image: linear-gradient(to bottom, transparent 0%, #000 22%, #000 100%);
}

.step-illustration img {
  position: absolute;
  left: 50%;
  top: 12px;
  width: 300px;
  max-width: none;
  transform: translateX(-50%) rotate(-4deg);
  transform-origin: 50% 100%;
  filter: drop-shadow(0 24px 40px rgba(0, 0, 0, 0.45));
  transition: transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.step-card--illustrated:hover .step-illustration img {
  transform: translateX(-50%) translateY(-10px) rotate(-2deg);
}

@media (prefers-reduced-motion: reduce) {
  .step-illustration img,
  .step-card--illustrated:hover .step-illustration img {
    transition: none;
    transform: translateX(-50%) rotate(-4deg);
  }
}

.step-card-body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
}

.step-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 6px 12px 6px 10px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--app-accent) 26%, var(--app-border));
  background: color-mix(in srgb, var(--app-accent) 8%, var(--app-surface-strong));
  color: var(--app-link);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
}

.step-label-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--app-link);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--app-accent) 20%, transparent);
}

.step-card .step-card-title {
  margin: 6px 0 4px;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--app-heading);
  letter-spacing: -0.01em;
}

.step-card p {
  margin: 0;
  color: var(--app-text-muted);
  line-height: 1.65;
  font-size: 15px;
}

.faq-section {
  max-width: 820px;
  margin-left: auto;
  margin-right: auto;
}

.faq-item {
  padding: 18px 20px;
}

.faq-item + .faq-item {
  margin-top: 14px;
}

.faq-item summary {
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  list-style: none;
  color: var(--app-text);
}

.faq-item summary::-webkit-details-marker {
  display: none;
}

.faq-item p {
  margin-top: 12px;
}

@media (max-width: 900px) {
  .steps-grid {
    grid-template-columns: 1fr;
  }

  .feature-grid .feature-card {
    flex-basis: min(55vw, 220px);
  }

  .home {
    width: min(100%, calc(100% - 24px));
  }

  .content-section h2 {
    font-size: 26px;
  }

  .step-card {
    min-height: 180px;
    padding: 22px 22px 24px;
  }

  /* One column on a phone: the stacked layout would put a 300px phone
     under every card. The text keeps the left, the phone comes in from
     the right edge, cropped, and the card stays a card. */
  .step-card--illustrated {
    min-height: 260px;
    padding: 22px 42% 24px 22px;
  }

  .step-card--illustrated .step-illustration {
    left: auto;
    right: -34px;
    top: 0;
    bottom: 0;
    width: 52%;
    height: auto;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 30%, #000 100%);
    mask-image: linear-gradient(to right, transparent 0%, #000 30%, #000 100%);
  }

  .step-card--illustrated .step-illustration img {
    left: 50%;
    top: 22px;
    width: 210px;
    transform: translateX(-50%) rotate(-6deg);
  }

  .step-card--illustrated:hover .step-illustration img {
    transform: translateX(-50%) rotate(-6deg);
  }

  .step-card--illustrated .step-ghost-number {
    display: none;
  }

  .step-ghost-number {
    font-size: 110px;
    top: -14px;
    right: -4px;
  }

  .step-card .step-card-title {
    font-size: 20px;
  }
}

@keyframes blink-caret {
  0%,
  49% {
    opacity: 1;
  }

  50%,
  100% {
    opacity: 0;
  }
}

#features,
#how-it-works,
#faq {
  scroll-margin-top: 84px;
}

.seo-section .seo-copy {
  max-width: 720px;
  margin: 0 auto;
}
.seo-section .seo-copy p {
  margin: 0 0 16px;
  line-height: 1.7;
  color: var(--app-text-muted);
  font-size: 15px;
}
/* Intro en exergue : plus grande, dans la couleur pleine du texte. */
.seo-section .seo-copy > p:first-of-type {
  font-size: 18px;
  line-height: 1.65;
  color: var(--app-text);
}
.seo-section .seo-copy h3 {
  position: relative;
  margin: 34px 0 12px;
  padding-left: 16px;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--app-heading);
}
/* Trait d'accent devant chaque sous-titre — même langage que les step-labels. */
.seo-section .seo-copy h3::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.18em;
  bottom: 0.18em;
  width: 3px;
  border-radius: 3px;
  background: var(--app-accent);
}
.seo-links {
  max-width: 720px;
  margin: 36px auto 0;
}
.seo-links h3 {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-muted);
}
.seo-links ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.seo-links a {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid var(--app-border);
  border-radius: 999px;
  background: var(--app-surface);
  color: var(--app-text);
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 160ms ease, color 160ms ease, background 160ms ease;
}
.seo-links a:hover {
  border-color: color-mix(in srgb, var(--app-accent) 45%, var(--app-border));
  color: var(--app-link);
  background: color-mix(in srgb, var(--app-accent) 6%, var(--app-surface));
}
</style>
